import { isMobile } from '@/consts/device';

import { getAppStore, setAppStore } from '@/store';
import { librarySectionsQueryOptions } from '@/hooks/queries/useLibrarySectionsQuery';
import { queryClient } from '@/utils/query/queryClient';
import normalizeCategorySlug from '@/utils/ui/normalizeCategorySlug';
import selectPresetBySlug from '@/utils/preset/selectPresetBySlug';
import { Screen } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type CategoryDeeplink = Extract<Deeplink, { kind: DeeplinkKind.Category }>;

/**
 * Category deeplink: open the library at the named category and, on desktop,
 * load its first preset. Section order is `pinDeeplinkSections`' job.
 */
const categoryHandler = async ({
  category,
}: CategoryDeeplink): Promise<void> => {
  const categorySlug = normalizeCategorySlug(category);
  if (!categorySlug) return;

  const { activeTabSlug } = getAppStore();
  setAppStore({
    ...(activeTabSlug !== categorySlug && { activeTabSlug: categorySlug }),
    screen: Screen.Library,
    shouldScrollToActiveTab: true,
  });

  if (isMobile) return;

  setAppStore({ hasAutoSelectedPreset: true, isPresetLoading: true });

  try {
    const sections = await queryClient.fetchQuery(librarySectionsQueryOptions);
    const firstSlug = sections?.find((section) => section.slug === categorySlug)
      ?.items[0]?.slug;

    if (!firstSlug) {
      setAppStore({ hasAutoSelectedPreset: false, isPresetLoading: false });
      return;
    }

    await selectPresetBySlug(firstSlug, queryClient);
  } catch {
    setAppStore({ hasAutoSelectedPreset: false, isPresetLoading: false });
  }
};

export default categoryHandler;
